import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { ApplicationError } from "../../../models/errors/application-error";
import WhatsappCommunicationsService, {
  WhatsappConversation,
  WhatsappMessage,
} from "../../../services/organisation/whatsapp-communications-service";

interface CommunicationsState {
  conversations: WhatsappConversation[];
  selectedConversationId: number | null;
  messages: WhatsappMessage[];
  searchText: string;
  replyText: string;
  loadingConversations: boolean;
  loadingMessages: boolean;
  sendingReply: boolean;
  downloadingMediaId: string | null;
  errorMessage: string | null;
}

const initialState: CommunicationsState = {
  conversations: [],
  selectedConversationId: null,
  messages: [],
  searchText: "",
  replyText: "",
  loadingConversations: false,
  loadingMessages: false,
  sendingReply: false,
  downloadingMediaId: null,
  errorMessage: null,
};

export const fetchConversations = createAsyncThunk<
  WhatsappConversation[],
  void,
  { state: RootState; rejectValue: string }
>("communications/fetchConversations", async (_, { rejectWithValue }) => {
  const communicationsService = new WhatsappCommunicationsService();

  try {
    const response = await communicationsService.getConversations();
    return response.conversations ?? [];
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const fetchMessages = createAsyncThunk<
  WhatsappMessage[],
  number,
  { state: RootState; rejectValue: string }
>("communications/fetchMessages", async (conversationId, { rejectWithValue }) => {
  const communicationsService = new WhatsappCommunicationsService();

  try {
    return await communicationsService.getMessages(conversationId);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const sendReply = createAsyncThunk<
  WhatsappMessage,
  { conversationId: number; message: string },
  { state: RootState; rejectValue: string }
>("communications/sendReply", async ({ conversationId, message }, { dispatch, rejectWithValue }) => {
  const communicationsService = new WhatsappCommunicationsService();

  try {
    const sentMessage = await communicationsService.replyToConversation(conversationId, message);
    void dispatch(fetchConversations());
    void dispatch(fetchMessages(conversationId));
    return sentMessage;
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

export const downloadConversationMedia = createAsyncThunk<
  Blob,
  { mediaId: string; mimeType?: string | null },
  { state: RootState; rejectValue: string }
>("communications/downloadConversationMedia", async ({ mediaId, mimeType }, { rejectWithValue }) => {
  const communicationsService = new WhatsappCommunicationsService();

  try {
    return await communicationsService.downloadMedia(mediaId, mimeType);
  } catch (error: any) {
    const apiError = ApplicationError.handleApiError(error, {});
    return rejectWithValue(apiError);
  }
});

const communicationsSlice = createSlice({
  name: "communications",
  initialState,
  reducers: {
    setSelectedConversationId: (state, action: PayloadAction<number | null>) => {
      state.selectedConversationId = action.payload;
      state.messages = [];
      state.replyText = "";
      state.errorMessage = null;
    },
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    setReplyText: (state, action: PayloadAction<string>) => {
      state.replyText = action.payload;
    },
    resetCommunications: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loadingConversations = true;
        state.errorMessage = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loadingConversations = false;
        state.conversations = action.payload;

        const selectedConversationExists =
          state.selectedConversationId !== null &&
          action.payload.some((conversation) => conversation.id === state.selectedConversationId);

        if (!selectedConversationExists) {
          const nextSelectedConversationId = action.payload[0]?.id ?? null;
          const selectionChanged = nextSelectedConversationId !== state.selectedConversationId;

          state.selectedConversationId = nextSelectedConversationId;

          if (selectionChanged) {
            state.messages = [];
            state.replyText = "";
          }
        }
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loadingConversations = false;
        state.errorMessage = action.payload ?? "Failed to fetch conversations";
      })
      .addCase(fetchMessages.pending, (state) => {
        state.loadingMessages = true;
        state.errorMessage = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loadingMessages = false;
        state.messages = action.payload;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loadingMessages = false;
        state.messages = [];
        state.errorMessage = action.payload ?? "Failed to fetch conversation messages";
      })
      .addCase(sendReply.pending, (state) => {
        state.sendingReply = true;
        state.errorMessage = null;
      })
      .addCase(sendReply.fulfilled, (state) => {
        state.sendingReply = false;
        state.replyText = "";
      })
      .addCase(sendReply.rejected, (state, action) => {
        state.sendingReply = false;
        state.errorMessage = action.payload ?? "Failed to send message";
      })
      .addCase(downloadConversationMedia.pending, (state, action) => {
        state.downloadingMediaId = action.meta.arg.mediaId;
      })
      .addCase(downloadConversationMedia.fulfilled, (state) => {
        state.downloadingMediaId = null;
      })
      .addCase(downloadConversationMedia.rejected, (state, action) => {
        state.downloadingMediaId = null;
        state.errorMessage = action.payload ?? "Failed to download media attachment";
      });
  },
});

export const {
  setSelectedConversationId,
  setSearchText,
  setReplyText,
  resetCommunications,
} = communicationsSlice.actions;

export default communicationsSlice.reducer;